/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { showElementCss } from '../../../utils/responsive';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { getPrefilledSolutionValue } from '../../../utils/getPrefilledSolutionValue';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import OpenEnquiryFormButton from '../../ui/OpenEnquiryFormButton';
import Picture from '../../ui/Picture';
import { colorsPalette, keyColors } from '../../../styles/colors';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import { Column, Container, MediaQueries, Row } from '../../../styles/Grid';
import {
  containerPaddingCss,
  contentTextCss,
  headlineCss,
  imageCss,
  spacerMarginCss,
  upperHeadlineCss,
} from './index.styles';

const defaultData = {
  BackgroundColor: {
    fields: {
      Value: {
        value: 'black',
      },
    },
  },
  UpperHeadline: {
    value: 'Price & availability',
  },
  Headline: {
    value: 'Let’s find the right workspace for your business',
  },
  ContentText: {
    value:
      'We have office space and more to suit all budgets, in every city of the world. Our team are ready to help you find the right place for you.',
  },
  GetInTouchCTA: {
    value: {
      href: '',
      linktype: '',
      text: '',
      anchor: '',
      querystring: '',
      target: '',
    },
  },
  Image: {
    value: {},
  },
  InvertedLayout: {
    value: false,
  },
};

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
  },
};

export class TextAndCTA extends SafePureComponent {
  /**
   * Create array containing number of columns depending of value of the arg.
   * @param {bool} hasImage
   * @return {Array}
   */
  getContentColumns(hasImage = false) {
    return hasImage
      ? [
          { sm: 6, md: 8, lg: 6, xl: 6 },
          { sm: 6, md: 7, lg: [6, 6], xl: [6, 6] },
          { sm: 4, md: 3, lg: [3, 6], xl: [3, 6] },
        ]
      : [
          { sm: 6, md: 8, lg: 10, xl: 10 },
          { sm: 6, md: 7, lg: [10, 10], xl: [10, 10] },
          { sm: 4, md: 3, lg: [3, 10], xl: [2, 10] },
        ];
  }

  renderGetInTouchCTA(linkObject) {
    const prefilledSolutionValue = getPrefilledSolutionValue(
      this.props.sitecoreContext,
    );
    const openEnquiryFormButtonProps = {
      linkObject,
      prefilledValues: {
        Solution: prefilledSolutionValue,
      },
    };
    const componentName = get(this.props, 'rendering.componentName', '');

    return (
      <OpenEnquiryFormButton
        {...openEnquiryFormButtonProps}
        componentName={componentName}
      />
    );
  }

  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);

    const {
      BackgroundColor,
      ContentText,
      GetInTouchCTA,
      Headline,
      Image,
      InvertedLayout,
      UpperHeadline,
    } = fields;
    const { black, white } = colorsPalette;
    const hasImage = get(Image, 'value.src', false);
    const isInverted = get(InvertedLayout, 'value', false);
    const [
      contentColums,
      innerContentColumns,
      innerContentCtaColumns,
    ] = this.getContentColumns(hasImage);
    const theme = get(BackgroundColor, 'fields.Value.value', keyColors.black);

    return (
      <Spacer
        marginCss={spacerMarginCss}
        paddingCss={containerPaddingCss}
        backgroundColor={theme}
      >
        <Container
          css={{
            color: theme === keyColors.black ? white : black,
          }}
        >
          <Row
            css={
              isInverted && {
                flexDirection: 'row-reverse',
              }
            }
          >
            <Column cols={contentColums} offsets={{ lg: 1 }}>
              <Row>
                <Column cols={innerContentColumns}>
                  <h2 css={upperHeadlineCss}>
                    <Text field={UpperHeadline} />
                  </h2>
                  <h3 css={headlineCss}>
                    <Text field={Headline} />
                    <DotComponent />
                  </h3>
                </Column>
                <Column css={contentTextCss(theme)} cols={innerContentColumns}>
                  <Text field={ContentText} />
                </Column>
                <Column cols={innerContentCtaColumns}>
                  {this.renderGetInTouchCTA(GetInTouchCTA)}
                </Column>
              </Row>
            </Column>
            {hasImage && (
              <Column
                css={showElementCss(MediaQueries.lgAndUp)}
                cols={{ sm: 6, md: 8, lg: 4 }}
                offsets={{ lg: 1 }}
              >
                <Picture
                  image={Image}
                  imageSizes={imageSizes}
                  imageCss={imageCss}
                />
              </Column>
            )}
          </Row>
        </Container>
      </Spacer>
    );
  }
}

TextAndCTA.defaultProps = {
  fields: defaultData,
};

TextAndCTA.displayName = 'TextAndCTA';

TextAndCTA.propTypes = {
  fields: PropTypes.shape({
    BackgroundColor: PropTypes.shape({
      fields: PropTypes.shape({
        Value: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    }),
    ContentText: PropTypes.shape({
      value: PropTypes.string,
    }),
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),
    Image: PropTypes.shape({
      value: PropTypes.shape({
        alt: PropTypes.string,
        src: PropTypes.string,
      }),
    }),
    InvertedLayout: PropTypes.shape({
      value: PropTypes.bool,
    }),
    UpperHeadline: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
};

export default withSitecoreContext()(TextAndCTA);
